import { Component, Output, EventEmitter, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { ExportHistoryModalComponent } from "../export-history-modal/export-history-modal.component";
import { ToasterService } from "../../services/toaster.service";
import { Helper } from "src/app/core/helpers/helper";

export const exportItemType = {
  available: 1,
  selected: 2
};

@Component({
  selector: "app-export-modal",
  templateUrl: "./export-modal.component.html",
  styleUrl: "./export-modal.component.scss",
  standalone: false
})
export class ExportModalComponent {
  availableExportItems: Array<any> = [];
  selectedExportItems: Array<any> = [];
  allItems: any;
  historyType: any;

  exportItemType = exportItemType;

  searchAvailableItem: string = "";
  searchSelectedItem: string = "";
  exportType: string = "";

  @Output() exportDataEvent = new EventEmitter();

  @ViewChild("modal", { static: true }) modal!: TemplateRef<any>;
  @ViewChild("exportHistoryModal", { static: true }) exportHistoryModal!: ExportHistoryModalComponent;

  constructor(public offcanvasService: NgbOffcanvas, private toasterService: ToasterService, private helper: Helper) {}

  /**
   * Opens a modal dialog to display the provided data.
   * Initializes the available and selected export item arrays based on the provided data.
   * Filters the items from the data into two categories: mandatory and non-mandatory.
   * The mandatory items are added to the selected export items array, while the non-mandatory items are added to the available export items array.
   * Opens the modal dialog using the offcanvas service.
   *
   * @param selectedExportItems The selected export items.
   * @param availableExportItems The available export items.
   * @param historyType The type of history.
   * @param exportType The type of export.
   * @returns void
   */
  openModal(selectedExportItems: any, availableExportItems: any, historyType: any, exportType: string = ""): void {
    this.exportType = exportType;
    this.allItems = [...selectedExportItems, ...availableExportItems]; // Store all items from the provided data

    this.availableExportItems = availableExportItems;
    this.selectedExportItems = selectedExportItems;

    this.historyType = historyType;

    this.offcanvasService.open(this.modal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => {
        // Handle close/dismiss result if needed
      },
      () => {
        // Handle dismiss reason if needed
      }
    );
  }

  /**
   * Opens a modal dialog to display the provided data.
   * Initializes the available and selected export item arrays based on the provided data.
   * Filters the items from the data into two categories: mandatory and non-mandatory.
   * The mandatory items are added to the selected export items array, while the non-mandatory items are added to the available export items array.
   * Opens the modal dialog using the offcanvas service.
   *
   * @param data The data to be displayed in the modal dialog.
   * @param historyType The type of history.
   * @param exportType The type of export.
   * @returns void
   */
  open(data: any, historyType: any, exportType: string = ""): void {
    this.exportType = exportType;
    this.allItems = data.tableHeader; // Store all items from the provided data

    this.availableExportItems = data.tableHeader.filter((x: any) => x.isHidden); // Filter non-mandatory items into available export items
    this.selectedExportItems = data.tableHeader.filter((x: any) => !x.isHidden); // Filter mandatory items into selected export items

    this.historyType = historyType;

    this.offcanvasService.open(this.modal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => {
        // Handle close/dismiss result if needed
      },
      () => {
        // Handle dismiss reason if needed
      }
    );
  }

  /**
   * Handles the click event when an export item is selected for export.
   * Moves the selected export item between the available and selected export item arrays based on the provided type.
   * If the type corresponds to available export items, it moves the item from the available list to the selected list.
   * If the type corresponds to selected export items and the item is not marked as mandatory, it moves the item from the selected list to the available list.
   *
   * @param type The type of export item (available or selected).
   * @param item The export item being interacted with.
   * @returns void
   */
  onExportItemClick(type: number, item: any): void {
    const { available, selected } = this.exportItemType;

    const addItemToArray = (sourceArray: any[], targetArray: any[], Item: any): void => {
      const newArray = sourceArray.filter((x) => x.key !== Item.key); // Create a new array without the item
      targetArray.push(Item); // Add the item to the target array
      sourceArray.length = 0; // Clear the contents of the source array
      sourceArray.push(...newArray); // Push all elements from newArray to sourceArray
    };

    if (type === available) {
      addItemToArray(this.availableExportItems, this.selectedExportItems, item); // Move item from available to selected
    } else if (type === selected && !item.mandatory) {
      addItemToArray(this.selectedExportItems, this.availableExportItems, item); // Move item from selected to available if not mandatory
    }
  }

  /**
   * Moves all export items between the available and selected lists based on the provided type.
   * Clears both available and selected export item arrays and then populates them based on the provided type.
   * If the type corresponds to available export items, it moves all items from the all items list to the selected list.
   * If the type corresponds to selected export items, it filters the items from the all items list into available and selected lists based on their mandatory status.
   *
   * @param type The type of export items to move (available or selected).
   * @returns void
   */
  onMoveAllClick(type: number): void {
    this.availableExportItems = []; // Clear the available export items array
    this.selectedExportItems = []; // Clear the selected export items array
    if (type === this.exportItemType.available) {
      this.selectedExportItems = JSON.parse(JSON.stringify(this.allItems)); // Move all items from all items list to selected list and use parse and stringify to unaffect allItems array
      this.availableExportItems = []; // Ensure available list is empty
    } else if (type === this.exportItemType.selected) {
      this.availableExportItems = this.allItems.filter((x: any) => !x.mandatory); // Filter non-mandatory items into available export items
      this.selectedExportItems = this.allItems.filter((x: any) => x.mandatory === true); // Filter mandatory items into selected export items
    }
  }

  /**
   * Closes the currently open modal dialog.
   *
   * @returns void
   */
  closeModal(): void {
    this.offcanvasService.dismiss(this.modal);
  }

  /**
   * Exports the selected data.
   * If no items are selected, shows an error notification.
   * Emits the selected export items.
   *
   * @returns void
   */
  exportData(): void {
    if (this.selectedExportItems.length === 0) {
      this.toasterService.showNotification("error", this.helper.translateService.instant("validation-title.please-select-atleast-one-to-export"));
      return;
    }
    const data: any = this.selectedExportItems;
    this.exportDataEvent.emit(data);
  }

  /**
   * Opens the export history modal.
   * Closes the current modal and opens the export history modal after a delay.
   *
   * @returns void
   */
  openExportHistoryModal(): void {
    this.closeModal();
    setTimeout(() => {
      this.exportHistoryModal.openModal(this.historyType, this.exportType);
    }, 500);
  }
}
